import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Calendar, Clock, Layers, MoreVertical, Pencil, Trash2 } from "lucide-react";

import { Badge } from "../../../components/Badge";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/DropdownMenu";
import { DataTable } from "../../../components/DataTable";

const TABLE_COLUMNS = [
  { key: "name", label: "Plantilla", tooltip: "Nombre de la plantilla" },
  { key: "type", label: "Tipo", tooltip: "Tipo de plantilla" },
  {
    key: "structure",
    label: "Estructura",
    tooltip: "Número de fases y tareas de la plantilla",
  },
  { key: "created", label: "Creación", tooltip: "Fecha de creación" },
  { key: "updated", label: "Actualización", tooltip: "Fecha de última actualización" },
  { key: "actions", label: "Acciones", tooltip: "Acciones disponibles" },
];

// Target del tutorial de proyectos personalizados (la tabla es la vista que usa).
const TABLE_SLOT_PROPS = {
  tableContainer: { "data-tutorial": "project-templates-list" },
};

const formatDate = (value) => {
  if (!value) return "Sin fecha";

  try {
    return new Date(value).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

function TemplateActionsCell({ template, onEdit, onDelete }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const canEdit = Boolean(template.can_edit);
  const isSetupTemplate = template.template_type?.toLowerCase() === "setup";

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip title="Ver acciones" arrow>
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{
            width: "28px",
            height: "28px",
            padding: 0,
            flexShrink: 0,
          }}
        >
          <MoreVertical size={16} />
        </IconButton>
      </Tooltip>

      <DropdownMenuContent
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        align="end"
        onClick={(event) => event.stopPropagation()}
      >
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleMenuClose();
            onEdit?.(template);
          }}
        >
          <Pencil size={16} />
          {canEdit ? "Editar" : "Ver"}
        </DropdownMenuItem>

        {canEdit && !isSetupTemplate && onDelete && (
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              handleMenuClose();
              onDelete(template);
            }}
            sx={{ color: "error.main" }}
          >
            <Trash2 size={16} />
            Eliminar
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </Stack>
  );
}

const captionSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: { xs: "0.7rem", md: "0.75rem" },
  whiteSpace: "nowrap",
};

export function ProjectTemplatesTableView({
  rows,
  totalCount,
  isFetching,
  currentPage,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onEdit,
  onDelete,
  getBadgeInfo,
  emptyMessage,
  toolbarProps,
  getRowProps,
}) {
  const theme = useTheme();

  const columns = useMemo(() => {
    const renderers = {
      name: (template) => (
        <Tooltip title={template.name || "Sin nombre"} arrow>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: { xs: "0.75rem", md: "0.875rem" },
            }}
          >
            {template.name || "Sin nombre"}
          </Typography>
        </Tooltip>
      ),
      type: (template) => {
        const badgeInfo = getBadgeInfo(template);
        return (
          <Badge
            sx={{
              backgroundColor: badgeInfo.background,
              color: badgeInfo.text,
              border: `1px solid ${badgeInfo.border}`,
            }}
          >
            {badgeInfo.label}
          </Badge>
        );
      },
      structure: (template) => (
        <Tooltip
          title={`${template.phases_count || 0} fases / ${template.tasks_count || 0} tareas`}
          arrow
        >
          <Typography variant="caption" sx={{ ...captionSx, color: theme.palette.text.secondary }}>
            <Layers size={12} color={theme.palette.text.secondary} />
            {`${template.phases_count || 0} / ${template.tasks_count || 0}`}
          </Typography>
        </Tooltip>
      ),
      created: (template) => (
        <Tooltip title={formatDate(template.created_at)} arrow>
          <Typography variant="caption" sx={captionSx}>
            <Clock size={12} color={theme.palette.text.secondary} />
            {formatDate(template.created_at)}
          </Typography>
        </Tooltip>
      ),
      updated: (template) => (
        <Tooltip title={formatDate(template.updated_at)} arrow>
          <Typography variant="caption" sx={captionSx}>
            <Calendar size={12} color={theme.palette.text.secondary} />
            {formatDate(template.updated_at)}
          </Typography>
        </Tooltip>
      ),
      actions: (template) => (
        <TemplateActionsCell template={template} onEdit={onEdit} onDelete={onDelete} />
      ),
    };

    return TABLE_COLUMNS.map((column) => ({
      key: column.key,
      header: column.label,
      tooltip: column.tooltip,
      align: column.key === "actions" ? "right" : "left",
      render: renderers[column.key],
      ...(column.key === "name" && { minWidth: { xs: 220, md: 320 }, width: { md: 320 } }),
    }));
  }, [theme, getBadgeInfo, onEdit, onDelete]);

  return (
    <DataTable
      {...toolbarProps}
      columns={columns}
      rows={rows}
      loading={isFetching}
      emptyIcon={Layers}
      emptyMessage={emptyMessage}
      onRowClick={onEdit ? (template) => onEdit(template) : undefined}
      getRowProps={getRowProps}
      slotProps={TABLE_SLOT_PROPS}
      tableMinWidth={980}
      maxHeight="calc(100vh - 290px)"
      minHeight="calc(100vh - 290px)"
      page={currentPage}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  );
}

ProjectTemplatesTableView.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalCount: PropTypes.number.isRequired,
  isFetching: PropTypes.bool,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  getBadgeInfo: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string,
  toolbarProps: PropTypes.object,
  getRowProps: PropTypes.func,
};

ProjectTemplatesTableView.defaultProps = {
  isFetching: false,
  onEdit: undefined,
  onDelete: undefined,
  emptyMessage: "No hay plantillas disponibles",
};

TemplateActionsCell.propTypes = {
  template: PropTypes.shape({
    can_edit: PropTypes.bool,
    template_type: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

TemplateActionsCell.defaultProps = {
  onEdit: undefined,
  onDelete: undefined,
};
